import React, {useState} from 'react';
import {ScrollView, TextInput, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import type {Discarded} from '../../../Hooks/LmsCourseApi/useCourseReview';
import {formatTimestamp} from '../courseFormat';
import {S, STATUS, T} from '../courseTheme';
import {ReviewFlag, ReviewPage, Signal} from '../reviewLogic';
import {E} from './editorTheme';
import {Button} from './ui';

type Props = {
  page: ReviewPage;
  flags: ReviewFlag[];
  /** Candidates the pipeline dropped near this chapter, with their index in the course list. */
  discarded: {index: number; item: Discarded}[];
  onDismiss: (flagId: number, reason: string) => void;
  onRestore: (index: number) => void;
};

const tone = (level: string) =>
  STATUS[level as keyof typeof STATUS] ?? STATUS.unknown;
const ICON: Record<string, string> = {
  good: 'checkmark-circle',
  warn: 'warning',
  bad: 'alert-circle',
};

function Heading({children}: {children: string}) {
  return (
    <CCText
      accessibilityRole="header"
      style={{
        fontSize: T.sm + 1,
        fontWeight: '700',
        color: E.ink,
        marginBottom: S[2],
      }}>
      {children}
    </CCText>
  );
}

function SignalRow({s}: {s: Signal}) {
  return (
    <View style={{flexDirection: 'row', gap: S[2], paddingVertical: 3}}>
      <Ionicons
        name={ICON[s[0]] ?? 'ellipse-outline'}
        size={16}
        color={tone(s[0]).fg}
        style={{marginTop: 2}}
      />
      <View style={{flex: 1}}>
        <CCText style={{fontSize: T.sm + 1, color: E.ink, lineHeight: 20}}>
          {s[1]}
        </CCText>
        {s[2] ? (
          <CCText style={{fontSize: T.sm, color: E.muted, lineHeight: 19}}>
            {s[2]}
          </CCText>
        ) : null}
      </View>
    </View>
  );
}

function FlagRow({
  flag,
  locked,
  onDismiss,
}: {
  flag: ReviewFlag;
  locked: boolean;
  onDismiss: (reason: string) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState(false);
  return (
    <View
      style={{
        gap: S[2],
        paddingVertical: S[2],
        borderTopWidth: 1,
        borderTopColor: E.line,
      }}>
      <View style={{flexDirection: 'row', gap: S[2]}}>
        <Ionicons
          name="flag"
          size={15}
          color={STATUS.warn.fg}
          style={{marginTop: 3}}
        />
        <View style={{flex: 1}}>
          <CCText
            style={{
              fontSize: T.sm + 1,
              fontWeight: '600',
              color: E.ink,
              lineHeight: 20,
            }}>
            {flag.kind.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())}
          </CCText>
          {flag.note ? (
            <CCText style={{fontSize: T.sm + 1, color: E.body, lineHeight: 20}}>
              {flag.note}
            </CCText>
          ) : null}
        </View>
      </View>
      {locked ? (
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
          <Ionicons name="lock-closed" size={14} color={STATUS.bad.fg} />
          <CCText style={{fontSize: T.sm, color: STATUS.bad.fg}}>
            A machine check fails here, so this can’t be marked fine.
          </CCText>
        </View>
      ) : asking ? (
        <View style={{gap: S[2]}}>
          <TextInput
            value={reason}
            onChangeText={t => {
              setReason(t);
              setMissing(false);
            }}
            accessibilityLabel="Reason for marking this fine"
            placeholder="Checked against the video — it is right"
            placeholderTextColor={E.muted}
            style={{
              minHeight: 44,
              borderWidth: 1,
              borderColor: missing ? STATUS.bad.fg : E.inputLine,
              borderRadius: 10,
              paddingHorizontal: S[3],
              fontSize: T.sm + 1,
              color: E.ink,
            }}
          />
          {missing ? (
            <CCText
              accessibilityLiveRegion="polite"
              style={{fontSize: T.sm, color: STATUS.bad.fg}}>
              Say why — the reason is listed when the course is published.
            </CCText>
          ) : null}
          <View style={{flexDirection: 'row', gap: S[2]}}>
            <Button
              label="Mark as fine"
              accessibilityLabel="Confirm dismiss"
              variant="accent"
              onPress={() => {
                if (!reason.trim()) {
                  setMissing(true);
                  return;
                }
                onDismiss(reason.trim());
                setAsking(false);
                setReason('');
              }}
            />
            <Button
              label="Cancel"
              variant="quiet"
              onPress={() => setAsking(false)}
            />
          </View>
        </View>
      ) : (
        <Button
          label="Mark as fine…"
          accessibilityLabel={`Mark as fine: ${flag.kind.replace(/_/g, ' ')}`}
          variant="secondary"
          style={{alignSelf: 'flex-start'}}
          onPress={() => setAsking(true)}
        />
      )}
    </View>
  );
}

/**
 * Everything the old review screen showed at once — the coach's words, the
 * machine checks and signals, the open flags, what was discarded nearby —
 * folded away until asked for. This is the only part of the chapter pane that
 * scrolls, and it never contains the board.
 */
export default function DetailsPanel({
  page,
  flags,
  discarded,
  onDismiss,
  onRestore,
}: Props) {
  const review = page.config?.review;
  const mine = flags.filter(
    f =>
      f.page_id === page.id &&
      (f.status === 'open' ||
        (f.status === 'dismissed' && !f.dismiss_reason?.trim())),
  );
  const hint = (page.config?.hint as string | undefined) ?? '';
  return (
    <ScrollView
      style={{flex: 1, minHeight: 0}}
      contentContainerStyle={{
        gap: S[5],
        paddingVertical: S[3],
        paddingHorizontal: S[1],
      }}>
      {review ? (
        <View>
          <Heading>What the coach said</Heading>
          {review.lead_in ? (
            <View style={{marginBottom: S[2]}}>
              <CCText
                style={{
                  fontSize: T.sm,
                  fontWeight: '600',
                  color: E.accent,
                  marginBottom: 2,
                }}>
                Before the board went up
              </CCText>
              <CCText
                selectable
                style={{fontSize: T.base, color: E.ink, lineHeight: 23}}>
                {review.lead_in}
              </CCText>
            </View>
          ) : null}
          <CCText
            selectable
            style={{
              fontSize: T.base,
              color: review.transcript ? E.ink : E.muted,
              lineHeight: 23,
            }}>
            {review.transcript || 'Nothing was said in this window.'}
          </CCText>
          {review.spoken_goal ? (
            <CCText
              style={{
                fontSize: T.sm,
                color: E.muted,
                marginTop: S[2],
              }}>{`The coach stated the task as: ${review.spoken_goal}.`}</CCText>
          ) : null}
        </View>
      ) : page.text ? (
        <View>
          <Heading>Chapter text</Heading>
          <CCText
            selectable
            style={{fontSize: T.base, color: E.ink, lineHeight: 23}}>
            {page.text}
          </CCText>
        </View>
      ) : null}

      {page.content_type === 'chess_position' ? (
        <View>
          <Heading>Hint</Heading>
          <CCText
            style={{
              fontSize: T.base,
              color: hint ? E.ink : E.muted,
              lineHeight: 22,
            }}>
            {hint || 'No hint yet.'}
          </CCText>
        </View>
      ) : null}

      {review?.machine?.length ? (
        <View>
          <Heading>Machine checks</Heading>
          {review.machine.map((s, i) => (
            <SignalRow key={`m${i}`} s={s} />
          ))}
        </View>
      ) : null}

      {review?.signals?.length ? (
        <View>
          <Heading>Why it was flagged</Heading>
          {review.signals.map((s, i) => (
            <SignalRow key={`s${i}`} s={s} />
          ))}
        </View>
      ) : null}

      <View>
        <Heading>
          {mine.length ? `Open flags · ${mine.length}` : 'Open flags'}
        </Heading>
        {mine.length ? (
          mine.map(f => (
            <FlagRow
              key={f.id}
              flag={f}
              locked={!!review?.hard}
              onDismiss={r => onDismiss(f.id, r)}
            />
          ))
        ) : (
          <CCText style={{fontSize: T.sm + 1, color: E.muted}}>
            None on this chapter.
          </CCText>
        )}
      </View>

      {discarded.length ? (
        <View>
          <Heading>Discarded nearby</Heading>
          {discarded.map(({index, item}) => (
            <View
              key={index}
              style={{
                gap: S[1],
                paddingVertical: S[2],
                borderTopWidth: 1,
                borderTopColor: E.line,
              }}>
              <CCText
                style={{fontSize: T.sm + 1, color: E.ink, lineHeight: 20}}>
                {[
                  item.time != null ? formatTimestamp(item.time) : null,
                  item.stage ? item.stage.replace(/_/g, ' ') : null,
                  item.reasons?.[0],
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </CCText>
              {item.fen ? (
                <Button
                  label="Restore as a puzzle"
                  accessibilityLabel={`Restore the candidate at ${
                    item.time != null
                      ? formatTimestamp(item.time)
                      : 'unknown time'
                  } as a puzzle`}
                  variant="secondary"
                  icon="add-circle-outline"
                  style={{alignSelf: 'flex-start'}}
                  onPress={() => onRestore(index)}
                />
              ) : null}
            </View>
          ))}
        </View>
      ) : null}
    </ScrollView>
  );
}
